import { useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, CheckCircle, AlertCircle, Mail } from "lucide-react";
import { Button, Card, CardContent } from "@tellescope/ui";
import { getApiURL } from "../../../config/api";

/**
 * Public unsubscribe page for email notifications.
 * Maps to /unsubscribe?enduserId={id}&type={type}
 *
 * This is a PUBLIC route - no authentication required.
 * The enduserId and type are passed as query params from the unsubscribe link in emails.
 *
 * TODO: If more public API mutations are needed in the future, consider creating a
 * usePublicMutation hook in @tellescope/tanstack-query to standardize the pattern.
 */
export const UnsubscribePage = () => {
  const { t } = useTranslation("unsubscribe");
  const [searchParams] = useSearchParams();

  const enduserId = searchParams.get("enduserId");
  const type = searchParams.get("type");

  const [isUnsubscribing, setIsUnsubscribing] = useState(false);
  const [isUnsubscribed, setIsUnsubscribed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUnsubscribe = useCallback(async () => {
    if (!enduserId || !type) return;

    setIsUnsubscribing(true);
    setError(null);

    try {
      const response = await fetch(`${getApiURL()}/v1/unsubscribe-enduser`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          enduserId,
          unsubscribeFrom: [type],
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to unsubscribe");
      }

      setIsUnsubscribed(true);
    } catch (err) {
      console.error("Unsubscribe failed:", err);
      setError(err instanceof Error ? err.message : "Failed to unsubscribe");
    } finally {
      setIsUnsubscribing(false);
    }
  }, [enduserId, type]);

  const handleClose = useCallback(() => {
    window.close();
  }, []);

  // Invalid link - missing required params
  if (!enduserId || !type) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <p className="text-sm font-medium text-destructive">{t("Unsubscribe link is invalid")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (isUnsubscribing) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <Loader2 className="h-8 w-8 mx-auto mb-4 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">{t("Unsubscribing...")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (isUnsubscribed) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
            <h1 className="text-xl font-semibold mb-2">{t("You have successfully unsubscribed")}</h1>
            <p className="text-sm text-muted-foreground mb-4">
              {t("You will no longer receive email notifications for portal chat messages.")}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("You can re-subscribe at any time by visiting your portal settings.")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Confirmation state
  return (
    <div className="flex items-center justify-center min-h-screen bg-background p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-6 text-center">
          <Mail className="h-12 w-12 mx-auto mb-4 text-primary" />
          <h1 className="text-xl font-semibold mb-2">{t("Unsubscribe from Portal Notifications?")}</h1>
          <p className="text-sm text-muted-foreground mb-4">
            {t("You are about to unsubscribe from email notifications for portal chat messages.")}
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            {t("You can re-subscribe at any time through your portal settings.")}
          </p>

          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={handleClose}>
              {t("Cancel")}
            </Button>
            <Button variant="destructive" onClick={handleUnsubscribe}>
              {t("Confirm Unsubscribe")}
            </Button>
          </div>

          {error && <p className="text-sm text-destructive mt-4">{error}</p>}
        </CardContent>
      </Card>
    </div>
  );
};
